import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import toast from 'react-hot-toast';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { uploadPhotos, createUploadToken } from './caseReviewApi';

const SECTION_PARAM_MAP = {
    lpn: 'lpn',
    tracking_label: 'trackingLabel',
    packing_slip: 'packingSlip',
    exterior_box: 'exteriorBox',
    interior_box: 'interiorBox',
    misc: 'misc'
};
const MULTI_FILE_SECTIONS = ['exterior_box', 'interior_box', 'misc'];

export default function PhotoUploadModal({ selectedCase, onClose, onUploaded }) {
    const [uploadSection, setUploadSection] = useState('misc');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [qrToken, setQrToken] = useState(null);

    // Fetch a short-lived, upload-scoped token for the QR code so the
    // long-lived session JWT is never encoded into the image.
    useEffect(() => {
        let cancelled = false;
        createUploadToken(selectedCase.qr_case_id)
            .then((data) => { if (!cancelled) setQrToken(data.token); })
            .catch(() => { if (!cancelled) setQrToken(null); });
        return () => { cancelled = true; };
    }, [selectedCase.qr_case_id]);

    const handleUpload = async () => {
        if (uploadFiles.length === 0) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('caseId', selectedCase.qr_case_id);
            const paramName = SECTION_PARAM_MAP[uploadSection] || 'misc';
            uploadFiles.forEach((file, index) => {
                if (MULTI_FILE_SECTIONS.includes(uploadSection)) {
                    formData.append(`${paramName}${index + 1}`, file);
                } else {
                    formData.append(paramName, file);
                }
            });

            await uploadPhotos(formData);
            toast.success(`Uploaded ${uploadFiles.length} photo(s)`);
            setUploadFiles([]);
            if (onUploaded) await onUploaded();
            onClose();
        } catch (e) {
            toast.error('Failed to upload photos');
        } finally {
            setUploading(false);
        }
    };

    // The QR encodes a short-lived (30 min) upload token minted by the backend
    // rather than the long-lived session JWT.
    const qrValue = qrToken
        ? `${process.env.REACT_APP_API_URL}/api/returns/createcase/uploadphotos?caseId=${selectedCase.qr_case_id}&QRToken=${encodeURIComponent(qrToken)}`
        : null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

                <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
                    <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Upload Photos - Case {selectedCase.qr_case_id}</h3>
                        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="px-6 py-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="text-center">
                                <h4 className="font-medium text-gray-900 mb-3">Scan to Upload from Phone</h4>
                                <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 inline-block">
                                    {qrValue ? (
                                        <QRCodeCanvas value={qrValue} size={180} level="H" includeMargin />
                                    ) : (
                                        <div className="w-[180px] h-[180px] bg-gray-100 animate-pulse rounded" aria-label="Generating QR code" />
                                    )}
                                </div>
                                <p className="text-sm text-gray-500 mt-2">Scan this QR code with your phone camera to upload photos directly</p>
                                <p className="text-xs text-gray-400 mt-1">Link expires in 30 minutes</p>
                            </div>

                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">Or Upload from Computer</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Photo Category</label>
                                        <select value={uploadSection} onChange={(e) => setUploadSection(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <option value="misc">Misc</option>
                                            <option value="exterior_box">Exterior Box</option>
                                            <option value="interior_box">Interior Box</option>
                                            <option value="lpn">LPN Label</option>
                                            <option value="tracking_label">Tracking Label</option>
                                            <option value="packing_slip">Packing Slip</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Photos</label>
                                        <input type="file" multiple accept="image/*" onChange={(e) => setUploadFiles(Array.from(e.target.files))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>

                                    {uploadFiles.length > 0 && (
                                        <div>
                                            <p className="text-sm text-gray-600 mb-2">Selected {uploadFiles.length} file(s):</p>
                                            <div className="flex flex-wrap gap-2">
                                                {uploadFiles.map((file, index) => (
                                                    <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">{file.name}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <button type="button" onClick={handleUpload} disabled={uploading || uploadFiles.length === 0} className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                        {uploading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <ArrowUpTrayIcon className="h-4 w-4" />
                                                Upload Photos
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 px-6 py-3 flex justify-end">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
